// components/ControlPanel.tsx

import { useState, useEffect, useRef, memo, useCallback } from 'react';
import { NOTES, SCALE_DISPLAY_NAMES } from '../utils/constants';
import { inferChordQuality } from '../utils/musicTheory';
import { 
  DEFAULT_OVERLAY_VISUALIZATIONS, 
  DEFAULT_CAGED_VISUALIZATIONS, 
  DEFAULT_CAGED_SHAPES, 
  DEFAULT_ROOT_CONFIG,
  type GuitarArrangement,
  type Note,
  type Scale,
  type OverlayQuality,
  type CagedQuality
} from '../presets/presetSchema';

// Auth and limitations
import { useAuth } from '../auth/AuthContext';
import { LimitationEngine } from '../utils/limitations';
import { ContentAnalyzer } from '../utils/contentAnalyzer';
import { PremiumBadge } from './auth/PremiumBadge';

// Define props interface
interface ControlPanelProps {
  arrangement: GuitarArrangement;
  setArrangement: (arrangement: GuitarArrangement) => void;
  onShowUpgrade?: (feature: string, message?: string) => void;
}

// Type for dropdown sections
type ActiveSection = 'scale' | 'overlayScale' | null;

export function ControlPanel({ arrangement, setArrangement, onShowUpgrade }: ControlPanelProps) {
  // Auth context for limitation checks
  const { user } = useAuth();
  
  const [activeSection, setActiveSection] = useState<ActiveSection>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Per-guitar dynamic content analysis
  const userTier = user?.tier || 'free';
  const guitarViolations = ContentAnalyzer.analyzeGuitars([arrangement], userTier);
  const isThisGuitarLocked = ContentAnalyzer.hasAnyViolations(guitarViolations);

  // Extract values from schema with fallbacks
  const rootNote = arrangement?.root?.note || DEFAULT_ROOT_CONFIG.note;
  const rootScale = arrangement?.root?.scale || DEFAULT_ROOT_CONFIG.scale;
  const showScaleDegrees = arrangement?.root?.showScaleDegrees || DEFAULT_ROOT_CONFIG.showScaleDegrees;
  const cagedActiveShapes = arrangement?.caged?.activeShapes || DEFAULT_CAGED_SHAPES;
  const cagedQuality = arrangement?.caged?.quality || 'MAJOR';
  const overlay = arrangement?.overlay; // Simple - either defined or undefined

  // Extract overlay visualizations from overlay (not from caged)
  const overlayVisualizations = overlay?.visualizations || DEFAULT_OVERLAY_VISUALIZATIONS;

  // Helper function to handle premium control panel clicks
  const handlePremiumControlPanelClick = useCallback(() => {
    if (onShowUpgrade) {
      // Show specific violation message
      if (guitarViolations.premiumScales.length > 0) {
        onShowUpgrade('scales', `Premium scales in use: ${guitarViolations.premiumScales.join(', ')}`);
      } else if (guitarViolations.overlayFeatures) {
        onShowUpgrade('overlay', 'The scale overlay system is a Premium feature!');
      } else {
        onShowUpgrade('premium', 'Unlock all premium features with Premium!');
      }
    }
  }, [onShowUpgrade, guitarViolations]);

  const currentCagedQuality = cagedQuality;

  // Function to convert quality name to display format with abbreviations
  const getDisplayName = (qualityName: string | undefined): string => {
    // Handle undefined or null qualityName
    if (!qualityName) {
      return 'Unknown';
    }
    
    // Use centralized display names from constants
    if (qualityName in SCALE_DISPLAY_NAMES) {
      return SCALE_DISPLAY_NAMES[qualityName as Scale];
    }
    
    // Fallback: Convert quality name to display format
    return qualityName
      .split('_')
      .map(word => word.charAt(0) + word.slice(1).toLowerCase())
      .join(' ');
  };

  // Click outside handler to close dropdowns
  const handleClickOutside = useCallback((event: MouseEvent) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
      setActiveSection(null);
    }
  }, []);

  // Escape key handler to close dropdowns
  const handleEscapeKey = useCallback((event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      setActiveSection(null);
    }
  }, []);

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscapeKey);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [handleClickOutside, handleEscapeKey]);

  const handleScaleChange = useCallback((rootNote: Note, quality: Scale): void => {
    // Check scale limitation
    const userTier = user?.tier || 'free';
    const scaleAccess = LimitationEngine.checkScaleAccess(quality, userTier);
    
    if (!scaleAccess) {
      if (onShowUpgrade) {
        onShowUpgrade('scales');
      } else {
        alert(LimitationEngine.getUpgradeMessage('scales'));
      }
      return;
    }

    const inferredCagedQuality = inferChordQuality(quality);
    
    setArrangement({
      ...arrangement,
      root: {
        ...arrangement.root,
        note: rootNote,
        scale: quality
      },
      // Update CAGED quality to match the inferred quality from the new scale
      caged: {
        activeShapes: arrangement.caged?.activeShapes || DEFAULT_CAGED_SHAPES,
        quality: inferredCagedQuality,
        visualizations: arrangement.caged?.visualizations || DEFAULT_CAGED_VISUALIZATIONS
      }
    });
    // Keep dropdown open so users can select multiple items
  }, [arrangement, user?.tier, onShowUpgrade, setArrangement]);

  const handleOverlayScaleChange = useCallback((rootNote: Note | 'none', quality: OverlayQuality | null): void => {
    // Check overlay feature access for free users
    if (rootNote !== 'none') {
      const userTier = user?.tier || 'free';
      const hasOverlayAccess = LimitationEngine.checkFeatureAccess('overlay', userTier);
      
      if (!hasOverlayAccess) {
        if (onShowUpgrade) {
          onShowUpgrade('overlay');
        } else {
          alert(LimitationEngine.getUpgradeMessage('overlay'));
        }
        return;
      }
    }

    const newOverlay = rootNote === 'none' ? undefined : { 
      note: rootNote as Note,
      quality: quality as OverlayQuality,
      visualizations: DEFAULT_OVERLAY_VISUALIZATIONS // Always use schema defaults
    };
    setArrangement({
      ...arrangement,
      overlay: newOverlay
    });
    // Keep dropdown open so users can select multiple overlay combinations
  }, [arrangement, user?.tier, onShowUpgrade, setArrangement]);

  const handleCagedQualityChange = useCallback((quality: CagedQuality): void => {
    setArrangement({
      ...arrangement,
            caged: {
        activeShapes: arrangement.caged?.activeShapes || DEFAULT_CAGED_SHAPES,
        quality,
        visualizations: arrangement.caged?.visualizations || DEFAULT_CAGED_VISUALIZATIONS
      }
    });
  }, [arrangement, setArrangement]);

  const toggleScaleDegrees = useCallback((): void => {
    // Scale degrees are now free for all users - no access check needed
    setArrangement({
      ...arrangement,
      root: {
        ...arrangement.root,
        showScaleDegrees: !showScaleDegrees
      }
    });
  }, [arrangement, showScaleDegrees, setArrangement]);
  
  const toggleCagedShape = useCallback((shape: keyof typeof DEFAULT_CAGED_SHAPES): void => {
    const isShapeCurrentlyActive = cagedActiveShapes?.[shape];
    const willBeActive = !isShapeCurrentlyActive;
    
    // CAGED shapes are unlimited for all users - no limitation check needed
    
    // Create caged object if it doesn't exist with defaults
    const currentCaged = arrangement.caged || {
      activeShapes: { ...DEFAULT_CAGED_SHAPES },
      quality: inferChordQuality(rootScale),
      visualizations: { ...DEFAULT_CAGED_VISUALIZATIONS }
    };
    
    const newActiveShapes = {
      ...currentCaged.activeShapes,
      [shape]: willBeActive
    };
    
    // Allow all shapes to be deactivated - don't remove the caged object
    // This preserves the ability to toggle shapes on/off freely
    
    setArrangement({
      ...arrangement,
      caged: {
        ...currentCaged,
        activeShapes: newActiveShapes
      }
    });
  }, [arrangement, cagedActiveShapes, rootScale, setArrangement]);

  const toggleOverlayVisualization = useCallback((vis: keyof typeof DEFAULT_OVERLAY_VISUALIZATIONS): void => {
    // For overlay visualizations, update them in the actual overlay
    if (!overlay) return;
    
    setArrangement({
      ...arrangement,
      overlay: {
        ...overlay,
        visualizations: {
          ...overlayVisualizations,
          [vis]: !overlayVisualizations?.[vis]
        }
      }
    });
  }, [arrangement, overlay, overlayVisualizations, setArrangement]);

  // Helper function to get button class name with premium disabled state
  const getScaleButtonClass = useCallback((scale: Scale): string => {
    const isActive = rootScale === scale;
    const baseClass = isActive ? 'active scale-button' : 'scale-button';
    
    // Add premium-disabled class for free users if scale is premium-only
    const userTier = user?.tier || 'free';
    if (userTier === 'free' && !LimitationEngine.checkScaleAccess(scale, userTier)) {
      return `${baseClass} premium-disabled`;
    }
    
    return baseClass;
  }, [rootScale, user?.tier]);

  // Helper function to get overlay button class name with premium disabled state  
  const getOverlayButtonClass = useCallback((note: Note, quality: OverlayQuality): string => {
    const isActive = overlay?.note === note && overlay?.quality === quality;
    const baseClass = isActive ? 'active scale-button' : 'scale-button';
    
    // Add premium-disabled class for free users (overlay is premium-only)
    const userTier = user?.tier || 'free';
    if (userTier === 'free' && !LimitationEngine.checkFeatureAccess('overlay', userTier)) {
      return `${baseClass} premium-disabled`;
    }
    
    return baseClass;
  }, [overlay?.note, overlay?.quality, user?.tier]);

  const toggleSection = useCallback((section: ActiveSection): void => {
    setActiveSection(activeSection === section ? null : section);
  }, [activeSection]);

  // Early return if arrangement is not provided
  if (!arrangement) {
    return <div className="control-panel">Loading...</div>;
  }

  return (
    <div className="control-panel" ref={dropdownRef}>
      <div className="scale-row">
        <div className="dropdown-section">
          <button 
            className="dropdown-header" 
            onClick={() => toggleSection('scale')}
            type="button"
            aria-haspopup="true"
            aria-expanded={activeSection === 'scale'}
          >
            <span className="dropdown-title">{rootNote} {getDisplayName(rootScale)}</span>
            <span className={`dropdown-arrow ${activeSection === 'scale' ? 'open' : ''}`}>▼</span>
          </button>
          {activeSection === 'scale' && (
            <div className="dropdown-content scale-dropdown-wide">
              <div className="overlay-header">
                <h4 className="section-title">Scale Selection</h4>
              </div>
              <div className="scale-sections-grid">
              {/* Root Note Selection */}
              <div className="scale-section">
                <h4 className="section-title">Root Note</h4>
                <div className="note-grid">
                  {NOTES.map(note => (
                    <button
                      key={note}
                      type="button"
                      className={rootNote === note ? 'active scale-button' : 'scale-button'}
                      onClick={() => {
                        handleScaleChange(note as Note, rootScale);
                      }}
                    >
                      {note}
                    </button>
                  ))}
                </div>
              </div>
            
              {/* Basic Scales */}
              <div className="scale-section">
                <h4 className="section-title">Basic Scales</h4>
                <div className="section-buttons">
                  <button type="button" className={getScaleButtonClass('MAJOR')} onClick={() => handleScaleChange(rootNote, 'MAJOR')}>Major</button>
                  <button type="button" className={getScaleButtonClass('MINOR')} onClick={() => handleScaleChange(rootNote, 'MINOR')}>Minor</button>
                  <button type="button" className={getScaleButtonClass('HARMONIC_MINOR')} onClick={() => handleScaleChange(rootNote, 'HARMONIC_MINOR')}>
                    Harmonic Minor
                    <PremiumBadge scale="HARMONIC_MINOR" inline />
                  </button>
                  <button type="button" className={getScaleButtonClass('MELODIC_MINOR')} onClick={() => handleScaleChange(rootNote, 'MELODIC_MINOR')}>
                    Melodic Minor
                    <PremiumBadge scale="MELODIC_MINOR" inline />
                  </button>
                  <button type="button" className={getScaleButtonClass('PENTATONIC_MAJOR')} onClick={() => handleScaleChange(rootNote, 'PENTATONIC_MAJOR')}>Pentatonic Major</button>
                  <button type="button" className={getScaleButtonClass('PENTATONIC_MINOR')} onClick={() => handleScaleChange(rootNote, 'PENTATONIC_MINOR')}>Pentatonic Minor</button>
                  <button type="button" className={getScaleButtonClass('BLUES')} onClick={() => handleScaleChange(rootNote, 'BLUES')}>Blues</button>
                </div>
              </div>
              
              <div className="scale-section">
                <h4 className="section-title">Modal Scales</h4>
                <div className="section-buttons">
                  <button type="button" className={getScaleButtonClass('DORIAN')} onClick={() => handleScaleChange(rootNote, 'DORIAN')}>Dorian</button>
                  <button type="button" className={getScaleButtonClass('PHRYGIAN')} onClick={() => handleScaleChange(rootNote, 'PHRYGIAN')}>Phrygian</button>
                  <button type="button" className={getScaleButtonClass('LYDIAN')} onClick={() => handleScaleChange(rootNote, 'LYDIAN')}>Lydian</button>
                  <button type="button" className={getScaleButtonClass('MIXOLYDIAN')} onClick={() => handleScaleChange(rootNote, 'MIXOLYDIAN')}>Mixolydian</button>
                  <button type="button" className={getScaleButtonClass('LOCRIAN')} onClick={() => handleScaleChange(rootNote, 'LOCRIAN')}>Locrian</button>
                  <button type="button" className={getScaleButtonClass('CHROMATIC')} onClick={() => handleScaleChange(rootNote, 'CHROMATIC')}>Chromatic</button>
                  <button type="button" className={getScaleButtonClass('WHOLE_TONE')} onClick={() => handleScaleChange(rootNote, 'WHOLE_TONE')}>Whole Tone</button>
                  <button type="button" className={getScaleButtonClass('OCTATONIC_HALF_WHOLE')} onClick={() => handleScaleChange(rootNote, 'OCTATONIC_HALF_WHOLE')}>Octatonic Half Whole</button>
                  <button type="button" className={getScaleButtonClass('OCTATONIC_WHOLE_HALF')} onClick={() => handleScaleChange(rootNote, 'OCTATONIC_WHOLE_HALF')}>Octatonic Whole Half</button>
                </div>
              </div>
              
              <div className="scale-section">
                <h4 className="section-title">Triads</h4>
                <div className="section-buttons">
                  <button type="button" className={getScaleButtonClass('MAJOR_TRIAD')} onClick={() => handleScaleChange(rootNote, 'MAJOR_TRIAD')}>Major</button>
                  <button type="button" className={getScaleButtonClass('MINOR_TRIAD')} onClick={() => handleScaleChange(rootNote, 'MINOR_TRIAD')}>Minor</button>
                  <button type="button" className={getScaleButtonClass('DIMINISHED_TRIAD')} onClick={() => handleScaleChange(rootNote, 'DIMINISHED_TRIAD')}>Diminished</button>
                  <button type="button" className={getScaleButtonClass('AUGMENTED_TRIAD')} onClick={() => handleScaleChange(rootNote, 'AUGMENTED_TRIAD')}>Augmented</button>
                </div>
              </div>
              
              <div className="scale-section">
                <h4 className="section-title">7th Chords</h4>
                <div className="section-buttons">
                  <button type="button" className={getScaleButtonClass('DOMINANT_7TH')} onClick={() => handleScaleChange(rootNote, 'DOMINANT_7TH')}>Dominant 7th</button>
                  <button type="button" className={getScaleButtonClass('MAJOR_7TH')} onClick={() => handleScaleChange(rootNote, 'MAJOR_7TH')}>Major 7th</button>
                  <button type="button" className={getScaleButtonClass('MINOR_7TH')} onClick={() => handleScaleChange(rootNote, 'MINOR_7TH')}>Minor 7th</button>
                  <button type="button" className={getScaleButtonClass('MINOR_MAJOR_7TH')} onClick={() => handleScaleChange(rootNote, 'MINOR_MAJOR_7TH')}>Minor Major 7th</button>
                  <button type="button" className={getScaleButtonClass('DIMINISHED_7TH')} onClick={() => handleScaleChange(rootNote, 'DIMINISHED_7TH')}>Diminished 7th</button>
                  <button type="button" className={getScaleButtonClass('HALF_DIMINISHED_7TH')} onClick={() => handleScaleChange(rootNote, 'HALF_DIMINISHED_7TH')}>Half Diminished 7th</button>
                  <button type="button" className={getScaleButtonClass('AUGMENTED_7TH')} onClick={() => handleScaleChange(rootNote, 'AUGMENTED_7TH')}>Augmented 7th</button>
                  <button type="button" className={getScaleButtonClass('AUGMENTED_MAJOR_7TH')} onClick={() => handleScaleChange(rootNote, 'AUGMENTED_MAJOR_7TH')}>Augmented Major 7th</button>
                </div>
              </div>
            </div>
          </div>
        )}
        </div>
        <button
          type="button"
          className={showScaleDegrees ? 'active degree-button' : 'degree-button'}
          onClick={toggleScaleDegrees}
          aria-pressed={showScaleDegrees}
          title="Toggle between note names and scale degrees"
        >
          º
        </button>
      </div>

      <div className="dropdown-section">
        <button 
          className="dropdown-header" 
          onClick={() => toggleSection('overlayScale')}
          type="button"
          aria-haspopup="true"
          aria-expanded={activeSection === 'overlayScale'}
          title="Overlay Scale: Highlight notes that appear in both your primary scale and the selected overlay scale"
        >
          <span className="dropdown-title">
            {/* Add visual indicator when overlay is active */}
            {overlay ? (
              <span>
                <span className="overlay-indicator">✨</span>{overlay.note} {getDisplayName(overlay.quality)}
              </span>
            ) : (
              <>
                None
                <PremiumBadge feature="overlay" inline />
              </>
            )}
          </span>
          <span className={`dropdown-arrow ${activeSection === 'overlayScale' ? 'open' : ''}`}>▼</span>
        </button>
        {activeSection === 'overlayScale' && (
          <div className="dropdown-content scale-dropdown-overlay">
            {/* Quick Select Grid - Primary Interface */}
            <div className="overlay-grid-section">
              <div className="overlay-header">
                <h4 className="section-title">Overlay Scale Selection</h4>
                <button 
                  type="button" 
                  className={!overlay ? 'active overlay-none-button' : 'overlay-none-button'} 
                  onClick={() => handleOverlayScaleChange('none', null)}
                >
                  None
                </button>
              </div>
              <div className="root-note-grid">
                {NOTES.map(note => (
                  <div key={note} className="root-note-section">
                    <h5 className="root-note-title">{note}</h5>
                    <div className="compact-buttons">
                      <button 
                        type="button" 
                        className={getOverlayButtonClass(note as Note, 'MAJOR_TRIAD')} 
                        onClick={() => handleOverlayScaleChange(note as Note, 'MAJOR_TRIAD')}
                        title={`${note} Major Triad`}
                      >
                        Maj
                      </button>
                      <button 
                        type="button" 
                        className={getOverlayButtonClass(note as Note, 'MINOR_TRIAD')} 
                        onClick={() => handleOverlayScaleChange(note as Note, 'MINOR_TRIAD')}
                        title={`${note} Minor Triad`}
                      >
                        Min
                      </button>
                      <button 
                        type="button" 
                        className={getOverlayButtonClass(note as Note, 'DOMINANT_7TH')} 
                        onClick={() => handleOverlayScaleChange(note as Note, 'DOMINANT_7TH')}
                        title={`${note} Dominant 7th Chord`}
                      >
                        Dom7
                      </button>
                      <button 
                        type="button" 
                        className={getOverlayButtonClass(note as Note, 'MAJOR_7TH')} 
                        onClick={() => handleOverlayScaleChange(note as Note, 'MAJOR_7TH')}
                        title={`${note} Major 7th Chord`}
                      >
                        Maj7
                      </button>
                      <button 
                        type="button" 
                        className={getOverlayButtonClass(note as Note, 'MINOR_7TH')} 
                        onClick={() => handleOverlayScaleChange(note as Note, 'MINOR_7TH')}
                        title={`${note} Minor 7th Chord`}
                      >
                        Min7
                      </button>
                      <button 
                        type="button" 
                        className={getOverlayButtonClass(note as Note, 'HALF_DIMINISHED_7TH')} 
                        onClick={() => handleOverlayScaleChange(note as Note, 'HALF_DIMINISHED_7TH')}
                        title={`${note} Half Diminished 7th Chord`}
                      >
                        m7♭5
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Overlay Visualization Options */}
      {overlay && (
        <div className="overlay-viz-section">
          <div className="compact-button-group">
            <button
              type="button"
              className={overlayVisualizations?.GLOW ? 'active viz-button' : 'viz-button'}
              onClick={() => toggleOverlayVisualization('GLOW')}
              title="Glow overlay highlighting"
            >Glow</button>
            <button
              type="button"
              className={overlayVisualizations?.GROW ? 'active viz-button' : 'viz-button'}
              onClick={() => toggleOverlayVisualization('GROW')}
              title="Grow overlay effect"
            >Grow</button>
            <button
              type="button"
              className={overlayVisualizations?.PULSE ? 'active viz-button' : 'viz-button'}
              onClick={() => toggleOverlayVisualization('PULSE')}
              title="Pulse overlay effect"
            >Pulse</button>
          </div>
        </div>
      )}

      <div className="caged-section">
        {!activeSection && (
          <div className="caged-button-row-full">
            {(['C', 'A', 'G', 'E', 'D'] as const).map(shape => (
              <button
                type="button"
                key={shape}
                className={cagedActiveShapes?.[shape] ? `active shape-button shape-${shape}` : `shape-button shape-${shape}`}
                onClick={() => toggleCagedShape(shape)}
              >
                {shape}
              </button>
            ))}
            <div className="chord-quality-toggle">
              <button
                type="button"
                className={currentCagedQuality === 'MAJOR' ? 'active quality-button' : 'quality-button'}
                onClick={() => handleCagedQualityChange('MAJOR')}
                title="Major chord quality"
              >
                M
              </button>
              <button
                type="button"
                className={currentCagedQuality === 'MINOR' ? 'active quality-button' : 'quality-button'}
                onClick={() => handleCagedQualityChange('MINOR')}
                title="Minor chord quality"
              >
                m
              </button>
            </div>
          </div>
        )}

      </div>

      {/* Premium Feature Lock - shows specific violations */}
      {isThisGuitarLocked && (
        <div className="premium-lock-indicator" onClick={handlePremiumControlPanelClick}>
          <div className="premium-lock-icon">🔒</div>
          <div className="premium-lock-text">Upgrade to Unlock</div>
        </div>
      )}

      {/* Premium hover overlay covers entire control panel */}
      {isThisGuitarLocked && (
        <div className="premium-hover-overlay" onClick={handlePremiumControlPanelClick}></div>
      )}

    </div>
  );
}

// Memoize ControlPanel component for performance optimization
// Re-renders only when props change
export default memo(ControlPanel, (prevProps, nextProps) => {
  // Custom comparison for arrangement object
  const arrangementEqual = 
    prevProps.arrangement.id === nextProps.arrangement.id &&
    JSON.stringify(prevProps.arrangement) === JSON.stringify(nextProps.arrangement);
  
  return arrangementEqual && 
         prevProps.setArrangement === nextProps.setArrangement &&
         prevProps.onShowUpgrade === nextProps.onShowUpgrade;
});
